import {
  useEffect, useMemo, useState,
} from 'react';
import { type DropdownOption } from '~/_shared/baseComponents/dropdown';
import { useDemographicDropdownOptions } from '~/_shared/components/customizeMetrics/useDemographicDropdownOptions';
import { DEFAULT_RANGES_COUNT } from '~/boundary/settings/useBoundaryFillSettingsPropsVariants';
import { type SpreadsheetColumnId } from '../../../../../../_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '../../../../../../_shared/utils/hooks';
import { usePrevious } from '../../../../../../_shared/utils/hooks/usePrevious';
import { useGetBoundaryGroup } from '../../../../../../store/boundaryGroups/boundaryGroups.selectors';
import {
  BoundaryTerritoryType,
  type CalculateBucketFunctionNumeric,
} from '../../../../../../store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { hasBoundaryFillTypeBoundaryData } from '../../boundaryTerritoryGroup.helpers';
import { getBoundaryTerritoryTypeDropdownOptions } from '../../fill/boundaryFill.helpers';
import { useSpreadsheetColumnsOptions } from '../../hooks/useSpreadsheetColumnsOptions';

type UseActiveBoundaryFillProps = {
  readonly boundaryGroupId: number | null;
  readonly fixedRangeCount?: number | null;
  readonly hiddenBoundaryTerritoryTypeOptions?: ReadonlyArray<BoundaryTerritoryType>;
  readonly initialBoundaryFillData?: SpreadsheetColumnId | null;
  readonly initialBoundaryTerritoryType?: BoundaryTerritoryType | null;
  readonly initialDemographicId?: string | null;
  readonly initialNumericCalculateBucketFunction?: CalculateBucketFunctionNumeric | null;
  readonly initialRangeCount?: number | null;
};

export const useActiveBoundaryFillEdit = ({
  boundaryGroupId,
  fixedRangeCount,
  hiddenBoundaryTerritoryTypeOptions,
  initialBoundaryFillData,
  initialBoundaryTerritoryType = null,
  initialDemographicId,
  initialNumericCalculateBucketFunction,
  initialRangeCount,
}: UseActiveBoundaryFillProps) => {
  const [boundaryTerritoryType, setBoundaryTerritoryType] = useState<BoundaryTerritoryType | null>(
    initialBoundaryTerritoryType ?? null);
  const [boundaryFillData, setBoundaryFillData] = useState<SpreadsheetColumnId | null>(initialBoundaryFillData ?? null);
  const [numericCalculateBucketFunction, setNumericCalculateBucketFunction] = useState<CalculateBucketFunctionNumeric | null>(
    initialNumericCalculateBucketFunction ?? null);
  const [selectedDemographicId, setSelectedDemographicId] = useState<string | null>(initialDemographicId ?? null);
  const [numberOfRanges, setNumberOfRanges] = useState(fixedRangeCount ?? initialRangeCount ?? DEFAULT_RANGES_COUNT);
  const [selectedDemographicCategory, setSelectedDemographicCategory] = useState<string | null>(null);
  const prevSelectedDemographyCategory = usePrevious(selectedDemographicCategory);
  const spreadsheetColumnOptions = useSpreadsheetColumnsOptions();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const [t] = useTranslation();

  const showDemographicGroup = boundaryTerritoryType === BoundaryTerritoryType.Demographic;
  const hideFillData = !hasBoundaryFillTypeBoundaryData(boundaryTerritoryType);

  const itemBoundaryGroup = boundaryGroupId !== null ? getBoundaryGroupById(boundaryGroupId) : null;

  const demographics = useMemo(() => itemBoundaryGroup?.demographics ?? [], [itemBoundaryGroup]);
  const { demographicOptions, demographicCategoryOptions } = useDemographicDropdownOptions(demographics, selectedDemographicCategory);

  const dataChanged =
    initialBoundaryTerritoryType !== boundaryTerritoryType ||
    initialBoundaryFillData !== boundaryFillData ||
    initialDemographicId !== selectedDemographicId ||
    numericCalculateBucketFunction !== null ||
    initialRangeCount !== numberOfRanges;

  const isUpdateDisabled: boolean = (
    boundaryTerritoryType === null ||
    (showDemographicGroup && (selectedDemographicCategory === null || selectedDemographicId === null)) ||
    (boundaryTerritoryType === BoundaryTerritoryType.Numeric && numericCalculateBucketFunction === null) ||
    (boundaryTerritoryType === BoundaryTerritoryType.Numeric && !boundaryFillData) ||
    (boundaryTerritoryType === BoundaryTerritoryType.Groups && !boundaryFillData)
  ) || !dataChanged;

  const boundaryTerritoryTypeOptions: ReadonlyArray<DropdownOption<BoundaryTerritoryType>> = useMemo(() => {
    const options = getBoundaryTerritoryTypeDropdownOptions(t);

    const newOptions = (demographics.length > 0) ?
      options :
      options.filter(item => item.value !== BoundaryTerritoryType.Demographic);

    return newOptions.filter(item => !hiddenBoundaryTerritoryTypeOptions?.includes(item.value));
  }, [t, demographics.length, hiddenBoundaryTerritoryTypeOptions]);

  // set boundary type to first available if the current is NOT on the list
  useEffect(() => {
    if ((boundaryTerritoryType === null) || (boundaryTerritoryTypeOptions.length === 0) || boundaryTerritoryTypeOptions.find(type => type.value === boundaryTerritoryType)) {
      return;
    }

    setBoundaryTerritoryType(boundaryTerritoryTypeOptions[0]?.value ?? null);
  }, [boundaryTerritoryType, boundaryTerritoryTypeOptions]);

  useEffect(() => {
    if (prevSelectedDemographyCategory !== null && prevSelectedDemographyCategory !== selectedDemographicCategory) {
      setSelectedDemographicId(null);
    }
  }, [selectedDemographicCategory, prevSelectedDemographyCategory]);

  useEffect(() => {
    setBoundaryTerritoryType(initialBoundaryTerritoryType ?? null);
  }, [initialBoundaryTerritoryType]);

  useEffect(() => {
    setBoundaryFillData(initialBoundaryFillData ?? null);
  }, [initialBoundaryFillData]);

  useEffect(() => {
    setSelectedDemographicId(null);
  }, [boundaryGroupId]);

  useEffect(() => {
    setSelectedDemographicId(initialDemographicId ?? null);
    const selectedDemographic = demographics.find(demographic => demographic.id === initialDemographicId);

    if (!selectedDemographic || initialDemographicId === null) {
      setSelectedDemographicCategory(null);
    }
    else {
      setSelectedDemographicCategory(selectedDemographic.category.toLowerCase());
    }
  }, [initialDemographicId, demographics]);

  return {
    isUpdateDisabled,
    hideFillData,
    showDemographicGroup,
    onBoundaryTerritoryTypeChange: setBoundaryTerritoryType,
    boundaryTerritoryTypeOptions,
    selectedDemographicCategory,
    onSelectedDemographicCategoryChange: setSelectedDemographicCategory,
    demographicCategoryOptions,
    onBoundaryFillDataChange: setBoundaryFillData,
    spreadsheetColumnOptions,
    onDemographicIdChange: setSelectedDemographicId,
    onNumberOfRangesChange: fixedRangeCount ? undefined : setNumberOfRanges,
    demographicOptions,
    boundaryFillModel: {
      numberOfRanges: fixedRangeCount ?? numberOfRanges,
      boundaryTerritoryType,
      boundaryFillData,
      selectedDemographicId,
      numericCalculateBucketFunction,
    } as const,
    onNumericCalculateBucketFunctionChange: setNumericCalculateBucketFunction,
  };
};
